import { paymentReleaseStatusDescriptions } from 'src/constants/invoice/constants'
import {
  PaymentReleaseActionKind,
  PaymentReleaseStatus,
  PartnerInvoiceStatus,
} from 'src/constants/invoice/enums'
import type {
  ComposedInvoice,
  ComposedPaymentReleaseSummary,
  PartnerInvoice,
  PaymentReleaseActionOption,
} from 'src/interfaces/invoice'

/**
 * Most pressing first — a failure needs a human, so it outranks everything else on the row. A
 * status this client does not know is ranked between these and `Released` (see
 * `getSummaryPriority`): it may be live, so it must never hide behind a finished release.
 */
const ACTIVE_SUMMARY_PRIORITY = [
  PaymentReleaseStatus.Failed,
  PaymentReleaseStatus.Processing,
  PaymentReleaseStatus.Scheduled,
]

const KNOWN_STATUSES: string[] = Object.values(PaymentReleaseStatus)

const PROCESSING_REASON = paymentReleaseStatusDescriptions[PaymentReleaseStatus.Processing]

const FAILED_REASON =
  'The last scheduled release failed and ops were alerted. Resolve it with ops before scheduling again.'

/**
 * What the row menu offers for an invoice's hold.
 *
 * Deliberately NOT gated on funding or repayment: funding is checked when the hold fires, so an
 * unfunded invoice can still be scheduled. Only a canceled invoice gets nothing — nothing can
 * move it, the same structural rule `getAvailableInvoiceActions` applies.
 *
 * - no hold / released → Schedule (a released hold is finished; the server answers 409
 *   NoPaymentsAwaitingReleaseError when nothing is left to hold)
 * - scheduled          → Reschedule (a POST that replaces the hold — never cancel-then-schedule)
 *                        and Cancel
 * - processing         → both shown but disabled: the server answers 409 to either
 * - failed             → Schedule shown but disabled: it needs a human, not a retry button
 */
export const getPaymentReleaseActions = (invoice: PartnerInvoice): PaymentReleaseActionOption[] => {
  if (invoice.status === PartnerInvoiceStatus.Canceled) {
    return []
  }

  switch (invoice.paymentReleaseStatus) {
    case PaymentReleaseStatus.Scheduled:
      return [
        { kind: PaymentReleaseActionKind.Reschedule },
        { kind: PaymentReleaseActionKind.Cancel },
      ]
    case PaymentReleaseStatus.Processing:
      return [
        { kind: PaymentReleaseActionKind.Reschedule, disabledReason: PROCESSING_REASON },
        { kind: PaymentReleaseActionKind.Cancel, disabledReason: PROCESSING_REASON },
      ]
    case PaymentReleaseStatus.Failed:
      return [{ kind: PaymentReleaseActionKind.Schedule, disabledReason: FAILED_REASON }]
    case PaymentReleaseStatus.Released:
    case null:
    case undefined:
      return [{ kind: PaymentReleaseActionKind.Schedule }]
    default:
      // A status this client does not know (the backend added one) is not "no hold": offering
      // Schedule could replace a live hold. Offer nothing until the client is updated.
      return []
  }
}

export const hasScheduledPaymentRelease = (invoice: PartnerInvoice): boolean => {
  return invoice.paymentReleaseStatus === PaymentReleaseStatus.Scheduled
}

export const countScheduledPaymentReleases = (
  invoices: PartnerInvoice[],
  invoiceIds: string[],
): number => {
  return invoices.filter((invoice) => {
    return invoiceIds.includes(invoice.id) && hasScheduledPaymentRelease(invoice)
  }).length
}

/** The statuses to look for, in order, with any unknown ones on this invoice slotted in. */
const getSummaryPriority = (invoice: ComposedInvoice): PaymentReleaseStatus[] => {
  const unknownStatuses = invoice.partnerInvoices.reduce<PaymentReleaseStatus[]>(
    (accumulator, partnerInvoice) => {
      const status = partnerInvoice.paymentReleaseStatus

      if (status && !KNOWN_STATUSES.includes(status) && !accumulator.includes(status)) {
        accumulator.push(status)
      }

      return accumulator
    },
    [],
  )

  return [...ACTIVE_SUMMARY_PRIORITY, ...unknownStatuses, PaymentReleaseStatus.Released]
}

/**
 * The most pressing hold on a composed invoice, or `null` when none of its invoices has one. An
 * unknown status is returned verbatim — `PaymentReleaseCell` renders it as text — never as "no
 * hold".
 */
export const summarizeComposedPaymentRelease = (
  invoice: ComposedInvoice,
): ComposedPaymentReleaseSummary | null => {
  for (const status of getSummaryPriority(invoice)) {
    const matching = invoice.partnerInvoices.filter((partnerInvoice) => {
      return partnerInvoice.paymentReleaseStatus === status
    })

    if (matching.length > 0) {
      return {
        status,
        count: matching.length,
        scheduledAt: matching.length === 1 ? matching[0].paymentReleaseScheduledAt ?? null : null,
      }
    }
  }

  return null
}
